<template>
    <div class="u-margin-bottom-narrow">
        <div class="page-block__item-wrapper" v-if="server && details" dusk="vps-details">
            <h2 class="page-block__title | u-font-weight-normal">{{ trans('page.vps.details.title') }}</h2>

            <button type="button"
                    class="btn btn--ghost | u-margin-right-narrow"
                    :disabled="isStateUpdating"
                    v-if="details.state === 'Stopped'"
                    @click="start()">
                {{ trans('page.vps.details.start') }}
            </button>

            <button type="button"
                    dusk="stop-button"
                    class="btn btn--ghost | u-margin-right-narrow"
                    :disabled="isStateUpdating"
                    v-if="details.state === 'Running'"
                    @click="stop()">
                {{ trans('page.vps.details.stop') }}
            </button>

            <button type="button"
                    dusk="reboot-button"
                    class="btn btn--ghost | u-margin-right-narrow"
                    :disabled="isStateUpdating"
                    v-if="details.state === 'Running'"
                    @click="reboot()">
                {{ trans('page.vps.details.reboot') }}
            </button>

            <button type="button"
                    dusk="console-button"
                    class="btn btn--ghost | u-margin-right-narrow"
                    :disabled="isStateUpdating"
                    v-if="details.state === 'Running' && server.url !== undefined"
                    @click="console()">
              {{ trans('page.vps.details.console') }}
            </button>

            <button type="button"
                    dusk="name-button"
                    class="btn btn--ghost | u-margin-right-narrow"
                    :disabled="isStateUpdating"
                    @click="showEditModal()">
                {{ trans('page.vps.details.name') }}
            </button>

            <button type="button"
                    dusk="reset-password-button"
                    class="btn btn--ghost | u-margin-right-narrow"
                    :disabled="isStateUpdating"
                    @click="showResetPasswordModal()">
                {{ trans('page.vps.details.password') }}
            </button>

            <button type="button"
                    dusk="upgrade-button"
                    class="btn btn--ghost | u-margin-right-narrow"
                    :disabled="isStateUpdating"
                    v-if="details.state === 'Stopped'"
                    @click="showUpgradesModal()">
                {{ trans('page.vps.details.upgrades') }}
            </button>

            <div class="page-block__item">
                <div class="page-block__item-content">
                    <div class="layout">
                        <div class="layout__item u-1/4-desk">
                            <span class="page-block__item-content-title"> {{ trans('page.vps.details.hostname') }} </span>
                        </div>
                        <div class="layout__item u-3/4-desk">{{ details.displayname }}</div>
                    </div>
                    <div class="layout">
                        <div class="layout__item u-1/4-desk">
                            <span class="page-block__item-content-title"> {{ trans('page.vps.details.displayname') }} </span>
                        </div>
                        <div class="layout__item u-3/4-desk">{{ server.data.display_name }}</div>
                    </div>
                    <div class="layout">
                        <div class="layout__item u-1/4-desk">
                            <span class="page-block__item-content-title"> {{ trans('page.vps.table.status') }} </span>
                        </div>
                        <div class="layout__item u-3/4-desk">{{ details.state }}</div>
                    </div>
                    <div class="layout">
                        <div class="layout__item u-1/4-desk">
                            <span class="page-block__item-content-title"> {{ trans('page.vps.details.ipv4') }} </span>
                        </div>
                        <div class="layout__item u-3/4-desk">{{ details.nic[0].ipaddress }}</div>
                    </div>
                    <div class="layout">
                        <div class="layout__item u-1/4-desk">
                            <span class="page-block__item-content-title"> {{ trans('page.vps.details.ipv6') }} </span>
                        </div>
                        <div class="layout__item u-3/4-desk">{{ details.nic[0].ip6address }}</div>
                    </div>
                </div>
            </div>
        </div>

        <edit-modal v-if="editModalVisible" @close="closeModal" :server="server" :domain="domain" />
        <reset-password-modal v-if="resetPasswordModalVisible" @close="closeModal" :server="server" :domain="domain" />
        <vps-upgrade v-if="upgradesModalVisible" @close="closeModal" :server="server" :domain="domain" />
    </div>
</template>

<script>
    import EditModal from "./EditModal.vue";
    import ResetPasswordModal from "./ResetPasswordModal";
    import VpsApi from '../services/api/vps.js';
    import VpsUpgrade from "./VpsUpgrade";
    import '../styles/_vps-overview.scss';

    export default {
        components: {
            ResetPasswordModal,
            VpsUpgrade,
            VpsApi,
            EditModal
        },
        props: {
            domain: {
                type: String,
                required: true
            },
            serverid: {
                type: String,
                required: true
            },
            vps: {
                type: Object
            },
            vpsdetails: {
                type: Object
            }
        },
        data: function () {
            return {
                isStateUpdating: false,
                targetState: null,
                targetName: null,
                numPolls: 0,
                polling: null,
                server: this.vps,
                details: this.vpsdetails,
                editModalVisible: false,
                upgradesModalVisible: false,
                resetPasswordModalVisible: false,
            };
        },
        /**
         * Called after the instance has been mounted.
         */
        mounted() {
            this.getVps();
            this.getVpsDetails();
        },
        methods: {
            showEditModal() {
                this.editModalVisible = true;
            },

            showResetPasswordModal() {
                this.resetPasswordModalVisible = true;
            },

            showUpgradesModal() {
                this.upgradesModalVisible = true;
            },

            closeModal() {
                this.editModalVisible = false;
                this.upgradesModalVisible = false;
                this.resetPasswordModalVisible = false;
            },

            getVps() {
                return VpsApi.vpsShow(
                    this.domain, this.serverid
                ).then(response => {
                    this.server = response.data;
                });
            },

            getVpsDetails() {
                return VpsApi.vpsDetails(
                    this.domain, this.serverid
                ).then(response => {
                    this.details = response.data;
                });
            },

            start() {
                return VpsApi.start(
                    this.domain, this.serverid
                ).then(() => {
                    this.targetState = 'Running';
                    this.pollStatus();
                });
            },

            stop() {
                return VpsApi.stop(
                    this.domain, this.serverid
                ).then(() => {
                    this.targetState = 'Stopped';
                    this.pollStatus();
                });
            },

            reboot() {
                return VpsApi.reboot(
                    this.domain, this.serverid
                ).then(() => {
                    this.targetState = 'Running';
                    this.pollStatus();
                });
            },

            console() {
                const url = this.server.url;
                const win = window.open(url, '_blank');
                win.focus();
            },

            pollStatus() {
                this.isStateUpdating = true;

                this.polling = setInterval(() => {
                    this.getVpsDetails();

                    this.numPolls++;

                    if (this.numPolls >= 15 || this.details.state === this.targetState) {
                        this.pollReset();
                    }
                }, 3000);
            },

            pollServerNameChange() {
                this.isStateUpdating = true;

                this.polling = setInterval(() => {
                    this.getVps();

                    this.numPolls++;

                    if (this.numPolls >= 15 || this.server.data.display_name === this.targetName) {
                        this.pollReset();
                    }
                }, 3000);
            },

            pollReset() {
                if (this.polling) {
                    clearInterval(this.polling);

                    this.targetState = null;
                    this.numPolls = 0;
                    this.isStateUpdating = false;
                }
            },

            beforeDestroy() {
                this.pollReset();
            }
        }
    };
</script>
